import { Injectable } from '@angular/core';
import { environment } from 'src/environments/environment';

declare let window: any;

@Injectable({
  providedIn: 'root',
})
export class CrispService {
  private isInitialized = false;
  private currentUser: any = null;

  private safePush(command: any[]): void {
    try {
      if (typeof window.$crisp !== 'undefined' && window.$crisp) {
        window.$crisp.push(command);
      }
    } catch (error) {
      console.warn('Commande Crisp ignorée suite à une erreur de format:', error);
    }
  }

  private isValidEmail(email: string): boolean {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  }

  constructor() {
    // Initialiser le Website ID avant le chargement du script
    if (environment.crispWebsiteId && environment.crispWebsiteId !== 'YOUR_WEBSITE_ID') {
      window.CRISP_WEBSITE_ID = environment.crispWebsiteId;
    }
  }

  /**
   * Vérifie si Crisp doit être chargé selon le domaine actuel
   * @returns true si Crisp doit être chargé, false sinon
   */
  shouldLoadCrisp(): boolean {
    const hostname = window.location.hostname;
    const origin = window.location.origin;

    // Ne pas charger Crisp sur localhost avec n'importe quel port
    if (hostname === 'localhost' || hostname === '127.0.0.1') {
      return false;
    }

    // Ne pas charger Crisp sur frontv2dev.myvesper.fr
    if (origin === 'https://frontv2dev.myvesper.fr' || hostname === 'frontv2dev.myvesper.fr') {
      return false;
    }

    return true;
  }

  initCrisp(): void {
    // Vérifier si Crisp doit être chargé selon le domaine
    if (!this.shouldLoadCrisp()) {
      console.log('Crisp désactivé pour ce domaine:', window.location.origin);
      return;
    }

    if (!environment.crispWebsiteId || environment.crispWebsiteId === 'YOUR_WEBSITE_ID') {
      console.warn('Crisp Website ID not configured');
      return;
    }

    // Définir le Website ID
    window.CRISP_WEBSITE_ID = environment.crispWebsiteId;

    // Vérifier si le script Crisp est déjà chargé
    if (document.querySelector('script[src="https://client.crisp.chat/l.js"]')) {
      // Le script est déjà chargé, vérifier si Crisp est initialisé
      if (typeof window.$crisp !== 'undefined' && window.$crisp) {
        this.isInitialized = true;
        // Masquer l'icône par défaut (elle sera affichée quand l'utilisateur sera connecté)
        this.hideCrispIcon();
        // Configurer l'écouteur d'événement pour le message de bienvenue
        this.setupWelcomeMessageListener();
        return;
      }
      // Attendre que Crisp soit initialisé
      this.waitForCrisp();
      return;
    }

    // Charger le script Crisp dynamiquement
    this.loadCrispScript();
  }

  private loadCrispScript(): void {
    const script = document.createElement('script');
    script.type = 'text/javascript';
    script.src = 'https://client.crisp.chat/l.js';
    script.async = true;
    script.onload = () => {
      this.waitForCrisp();
    };
    script.onerror = () => {
      console.error('Failed to load Crisp script');
    };
    document.getElementsByTagName('head')[0].appendChild(script);
  }

  private waitForCrisp(): void {
    const checkCrisp = setInterval(() => {
      if (typeof window.$crisp !== 'undefined' && window.$crisp) {
        this.isInitialized = true;
        clearInterval(checkCrisp);
        // Masquer l'icône par défaut (elle sera affichée quand l'utilisateur sera connecté)
        this.hideCrispIcon();
        // Configurer l'écouteur d'événement pour le message de bienvenue
        this.setupWelcomeMessageListener();
      }
    }, 100);

    // Timeout après 5 secondes
    setTimeout(() => {
      clearInterval(checkCrisp);
      if (!this.isInitialized) {
        console.warn('Crisp script failed to initialize');
      }
    }, 5000);
  }

  updateCrisp(user: any): void {
    // Vérifier si Crisp doit être chargé selon le domaine
    if (!this.shouldLoadCrisp()) {
      return;
    }

    if (!user) {
      return;
    }

    // Stocker les informations utilisateur pour le message de bienvenue
    this.currentUser = user;

    // Attendre que Crisp soit initialisé
    if (typeof window.$crisp === 'undefined' || !window.$crisp) {
      // Si Crisp n'est pas encore chargé, attendre un peu
      setTimeout(() => this.updateCrisp(user), 500);
      return;
    }

    const fullName = `${user.firstName || ''} ${user.lastName || ''}`.trim();
    const companyToSend = user.activeFirmName; // Récupère le nom du cabinet

    // 1. Identifier l'utilisateur dans Crisp (important !)
    const userEmail = (user.email || '').trim();
    if (userEmail && this.isValidEmail(userEmail)) {
      this.safePush(['set', 'user:email', [userEmail]]);
    }
    if (fullName) {
      this.safePush(['set', 'user:nickname', [fullName]]);
    }

    // Configuration des données de session (format Crisp attendu)
    this.safePush(['set', 'session:data', [[
      ['user_id', user.id?.toString() || ''],
      ['first_name', user.firstName || ''],
      ['last_name', user.lastName || ''],
      ['cabinet', companyToSend || ''],
      ['vesper_version', 'v2']
    ]]]);

    // Configuration de l'entreprise (format Crisp attendu)
    if (companyToSend) {
      this.safePush(['set', 'user:company', [companyToSend]]);
    }

    // Le message de bienvenue sera envoyé automatiquement via l'écouteur chat:opened
    // On ne l'envoie pas ici pour éviter les doublons

    // Afficher l'icône Crisp maintenant que l'utilisateur est connecté
    this.showCrispIcon();
  }

  /**
   * Configure l'écouteur d'événement pour envoyer le message de bienvenue
   * lorsque le chat est ouvert pour la première fois
   */
  private setupWelcomeMessageListener(): void {
    if (typeof window.$crisp === 'undefined' || !window.$crisp) {
      return;
    }

    // Écouter l'événement d'ouverture du chat pour envoyer le message si nécessaire
    this.safePush(['on', 'chat:opened', () => {
      if (this.currentUser) {
        const fullName = `${this.currentUser.firstName || ''} ${this.currentUser.lastName || ''}`.trim();
        const cabinet = this.currentUser.activeFirmName || '';
        this.sendWelcomeMessageOnce(fullName, cabinet);
      }
    }]);

    // Écouter aussi l'événement de session chargée pour envoyer le message si le chat est déjà ouvert
    // Cela gère le cas où l'utilisateur se connecte alors que le chat est déjà ouvert
    this.safePush(['on', 'session:loaded', () => {
      // Attendre un peu pour s'assurer que le chat est prêt
      setTimeout(() => {
        if (this.currentUser) {
          const fullName = `${this.currentUser.firstName || ''} ${this.currentUser.lastName || ''}`.trim();
          const cabinet = this.currentUser.activeFirmName || '';
          this.sendWelcomeMessageOnce(fullName, cabinet);
        }
      }, 1000);
    }]);
  }

  /**
   * Envoie automatiquement un message de bienvenue personnalisé dans Crisp
   * Format: "Bonjour [fullName] - [cabinet]"
   * Utilise message:show pour afficher un message du bot (pas message:send qui envoie au nom de l'utilisateur)
   */
  private sendWelcomeMessageOnce(fullName: string, cabinet: string): void {
    if (typeof window.$crisp === 'undefined' || !window.$crisp) {
      return;
    }

    // Vérifier si le message de bienvenue a déjà été envoyé dans cette session
    // Éviter que le message s'envoie à chaque navigation (Angular est une SPA)
    const welcomeSentKey = 'crisp_welcome_sent';
    
    // Marquer IMMÉDIATEMENT pour éviter les doublons en cas d'appels simultanés
    if (sessionStorage.getItem(welcomeSentKey)) {
      return; // Ne pas renvoyer le message si déjà envoyé
    }
    
    // Marquer immédiatement avant le setTimeout pour éviter les doublons
    sessionStorage.setItem(welcomeSentKey, 'true');

    // Construire le message de bienvenue
    // Format: "Bonjour [fullName] - [cabinet] 👋\nComment pouvons-nous vous aider ?"
    let welcomeMessage = 'Bonjour';
    
    if (fullName) {
      welcomeMessage += ` ${fullName}`;
    }
    
    if (cabinet) {
      welcomeMessage += ` - ${cabinet}`;
    }
    
    // Ajouter l'emoji et la deuxième ligne
    welcomeMessage += ' 👋\nComment pouvons-nous vous aider ?';

    // Envoyer le message de bienvenue automatiquement avec message:show
    // message:show affiche un message du bot Crisp côté utilisateur (ce qu'on veut)
    // message:send enverrait un message au nom de l'utilisateur (pas ce qu'on veut)
    setTimeout(() => {
      if (typeof window.$crisp !== 'undefined' && window.$crisp) {
        this.safePush([
          'do',
          'message:show',
          [
            'text',
            welcomeMessage
          ]
        ]);
      }
    }, 500);
  }

  shutdownCrisp(): void {
    if (typeof window.$crisp !== 'undefined' && window.$crisp) {
      // Déconnexion de l'utilisateur
      this.safePush(['do', 'session:reset']);
      // Masquer l'icône Crisp
      this.hideCrispIcon();
    }
    // Réinitialiser les données utilisateur
    this.currentUser = null;
    // Supprimer le flag de message de bienvenue envoyé
    sessionStorage.removeItem('crisp_welcome_sent');
  }

  hideCrispIcon(): void {
    if (typeof window.$crisp !== 'undefined' && window.$crisp) {
      // Masquer l'icône du widget Crisp
      this.safePush(['do', 'chat:hide']);
    }
  }

  showCrispIcon(): void {
    if (typeof window.$crisp !== 'undefined' && window.$crisp) {
      // Afficher l'icône du widget Crisp
      this.safePush(['do', 'chat:show']);
    }
  }

  showCrisp(): void {
    if (typeof window.$crisp !== 'undefined' && window.$crisp) {
      this.safePush(['do', 'chat:open']);
    }
  }

  hideCrisp(): void {
    if (typeof window.$crisp !== 'undefined' && window.$crisp) {
      this.safePush(['do', 'chat:close']);
    }
  }
}
